import { getHelper } from '@/devtools/utils';
import {
  SIMPLE_CHOICE,
  SIMPLE_CHOICE_META,
} from '@/features/ChoicesLists/Simple/types';

import { ROLE, useClientStore } from '@/features/Clients';
import {
  VersicherungsnehmerCompanyPropertyContractSchema,
  VersicherungsnehmerPropertyContractSchema,
} from '@/features/Clients/Schemas/Versicherungsnehmer';
import type { ComparisonDetail } from '@/features/Comparison/Components/types';
import { SEARCH_DATA_CONTEXT } from '@/features/Global/types';
import { nutzInputDehydrationDefinitions } from '@/features/Sparten/nutzv2/inputDehydration';

import type { InputStore } from '@/features/Sparten/nutzv2/useInputStore';
import { useNutzv2InputStore } from '@/features/Sparten/nutzv2/useInputStore';
import {
  filterCategories,
  filterItemsByMetaData,
} from '@/features/Sparten/shared/utils/featureFilter';
import type { SpartenConfig } from '@/features/Sparten/types';
import { dehydrateRequestData } from '@/utils/dehydrateRequestData/dehydrateRequestData';
import boolean from '@/utils/formatters/boolean';
import { formatCurrency } from '@/utils/formatters/euro';
import { formatChoice } from '@/utils/formatters/simpleChoice';
import string from '@/utils/formatters/string';
import { getSimpleChoiceMeta } from '@/utils/getSimpleChoiceMeta/getSimpleChoiceMeta';
import { useStorableState } from '@/utils/useStorableState/useStorableState';
import { kfzContractDehydrationDefinitions } from '@/features/Sparten/shared/contractDehydration/kfz/contractDehydration';
import {
  hasFahrerschutzversicherung,
  hasRabattschutzKfzHaftpflicht,
  hasRabattschutzKfzVollkasko,
  hasSfKlasseAndisVollkasko,
  hasSfKlasseReactive,
  isCompany,
  isNotHaftpflicht,
  isVollkasko,
} from '@/features/Sparten/shared/renderConditions/kfz/sharedRenderConditions';

import {
  kfzAlternateTariffSearchRequestIdentifier,
  kfzInventoryContractTitle,
  kfzQuotePreInputMergeHandler,
  kfzSpecifiedCompanyFilter,
} from '@/features/Sparten/shared/utils/kfzConfigUtils';
import type { Company } from '@/features/Companies/types';
import { kfzPreviousInsurance } from '@/features/Sparten/shared/utils/hasPreviousInsurance';
import { ENTITY_TYPE } from '@/features/EntityManager/types';
import { relevantPreviousInsuranceMissMatchTypes } from '@/features/Sparten/shared/kfz/relevantPreviousInsuranceMissMatchTypes';
import { clientInputDehydration } from '@/features/Sparten/nutzv2/clientDehydration';
import { calculationRelevantFahrerFields } from '@/features/Sparten/shared/kfz/calculationRelevantFahrerFields';
import { keepDataFromQuoteClientConfiguration } from '@/features/Sparten/shared/kfz/keepDataFromQuoteClientConfiguration';
import { kfzFahrerMetaSchema } from '@/features/Sparten/shared/schema/kfz/clientSchema/clientSchema';
import image from './assets/bg.jpg';
import { nutzVersicherungsangabenConfig } from '@/features/Sparten/nutzv2/versicherungsangabenConfig';

/**
 * SpartenConfig der Fake-Sparte NUTZ V2 — eine 1:1-Kopie der nutz-Config; abweichend sind nur
 * path/sparte/name, der V2-Input-Store (`useNutzv2InputStore`), `kfzVersion` und die eigene
 * Absicherungs-Section (Pflichtfeld `hauptfaelligkeitUnterjaehrig`).
 *
 * `nutzv2` wird im Backend vor dem Kernel-Boot auf `nutz` gemappt und über einen eigenen
 * kompilierten Container (Kernel-Variante) bedient. Ob und unter welchem Pfad die Sparte
 * geroutet wird, entscheidet `KFZ_MODE_NUTZ` (siehe `router/spartenRoutesFactory/applyKfzV2Switch`).
 */
export const config: SpartenConfig = {
  component: () => import('@/views/Base/Layout.vue'),
  path: '/nutzv2',
  props: {
    name: 'Nutzfahrzeugversicherung (V2)',
    sparte: 'nutzv2',
    image,
  },

  general: {
    maxSimultaneousCount: -1,

    kfzVersion: 2,

    versicherungsangabenConfig: nutzVersicherungsangabenConfig,

    distribution: {
      channel: true,
    },

    clientFields: {
      versicherungsnehmer: {
        anredeId: 'anredeId',
        geburtsdatum: 'geburtsdatum',
        familienstand: 'familienstand',
        plz: 'adresse.plz',
        nationalitaet: 'nationalitaet',
        fuehrerscheindatum: 'fuehrerscheindatum',
      },
    },

    calculationRelevantEntityFields: {
      [ENTITY_TYPE.CLIENT]: {
        versicherungsnehmer: [
          'geburtsdatum',
          'anredeId',
          'familienstand',
          'adresse.plz',
          'nationalitaet',
          'fuehrerscheindatum',
        ],
        abweichenderHalter: ['adresse.plz', 'adresse.ort', 'geburtsdatum'],
        fahrer1: calculationRelevantFahrerFields,
        fahrer2: calculationRelevantFahrerFields,
        fahrer3: calculationRelevantFahrerFields,
        fahrer4: calculationRelevantFahrerFields,
        fahrer5: calculationRelevantFahrerFields,
      },
    },

    profileFields: [
      'allgemein.wkz',
      'allgemein.versicherungsartSelection',
      'allgemein.versicherungsart',
      'fahrzeug.nutzungsart',
      'fahrzeug.auslandsnutzung',
      'versicherungsnehmer.isFirma',
      'kaskoTyp',
      'deckungssumme',
      'werkstattbindung',
      'tierbissfolgeschaeden',
      'fahrerschutzversicherung',
      'schutzbriefleistungen',
      'rabattschutzKfzHaftpflicht',
      'kollisionMitTieren',
      'gapDeckung',
      'kaskoSB.teilkasko',
      'kaskoSB.vollkasko',
      'kaskoSB.gleicheSB',
    ],

    isVersicherungsbeginnFromThePastAllowed: true,

    keepDataFromQuote: {
      clients: keepDataFromQuoteClientConfiguration,

      copyClientFieldsToInputStore: {
        'adresse.ort': 'versicherungsnehmer.ort',
      },
    },

    relevantPreviousInsuranceMissMatchTypes,

    clientEntityDehydration: {
      fahrer1: clientInputDehydration,
      fahrer2: clientInputDehydration,
      fahrer3: clientInputDehydration,
      fahrer4: clientInputDehydration,
      fahrer5: clientInputDehydration,
    },

    clientEntityMetaSchema: {
      fahrer1: kfzFahrerMetaSchema,
      fahrer2: kfzFahrerMetaSchema,
      fahrer3: kfzFahrerMetaSchema,
      fahrer4: kfzFahrerMetaSchema,
      fahrer5: kfzFahrerMetaSchema,
    },

    buildInputDataObject: (context: SEARCH_DATA_CONTEXT, dehydrate = false) => {
      const state = useNutzv2InputStore().$state as InputStore;

      const preparedData = useStorableState().create(state);

      if (context === SEARCH_DATA_CONTEXT.TARIFF && dehydrate) {
        return dehydrateRequestData(
          preparedData,
          nutzInputDehydrationDefinitions,
        );
      }

      return preparedData;
    },

    buildContractDataObject: (state: Record<string, unknown>) =>
      dehydrateRequestData(state, kfzContractDehydrationDefinitions),

    quotePreInputMergeCallback: (state) => kfzQuotePreInputMergeHandler(state),

    featureFilterFunction: () => {
      const filteredCategories = filterCategories();

      return filterItemsByMetaData(filteredCategories);
    },

    specifiedCompanyFilter: (): Company[] => kfzSpecifiedCompanyFilter(),

    getAlternateTariffSearchRequestIdentifier:
      kfzAlternateTariffSearchRequestIdentifier,

    inventoryContractTitle: kfzInventoryContractTitle,

    comparisonComponents: {
      HeaderComponent: () =>
        import('@/features/Sparten/shared/components/KfzComparisonHeader.vue'),
    },

    comparison: {
      getDetails: () => {
        const map = new Map<string, ComparisonDetail>();

        map.set('betrag', {
          label: 'zu zahlender Beitrag',
          format: formatCurrency,
        });

        return map;
      },
      serviceFields: [
        {
          label: 'Deckungssumme',
          fieldPath: 'deckungssumme',
          inputFormatter: formatChoice(SIMPLE_CHOICE.KFZ_DECKUNGSSUMME),
          formatter: string,
        },
        {
          label: 'Fahrerschutzversicherung',
          fieldPath: 'fahrerschutzversicherung',
          inputFormatter: boolean,
          formatter: string,
          requirement: () => hasFahrerschutzversicherung.value,
        },
        {
          label: 'Schutzbriefleistungen',
          fieldPath: 'schutzbriefleistungen',
          inputFormatter: boolean,
          formatter: string,
        },
        {
          label: 'Werkstattbindung',
          fieldPath: 'werkstattbindung',
          inputFormatter: formatChoice(SIMPLE_CHOICE.WERKSTATTBINDUNG),
          formatter: string,
          requirement: () => isNotHaftpflicht.value,
        },
        {
          label: 'Tierbissfolgeschäden',
          fieldPath: 'tierbissfolgeschaeden',
          inputFormatter: formatChoice(SIMPLE_CHOICE.TIERBISSFOLGESCHAEDEN),
          formatter: string,
          requirement: () => isNotHaftpflicht.value,
        },
        {
          label: 'Rabattschutz Kfz-Haftpflicht',
          fieldPath: 'rabattschutzKfzHaftpflicht',
          inputFormatter: boolean,
          formatter: string,
          requirement: () => hasRabattschutzKfzHaftpflicht.value,
        },
        {
          label: 'Kollision mit Tieren aller Art',
          fieldPath: 'kollisionMitTieren',
          inputFormatter: boolean,
          formatter: string,
          requirement: () => isNotHaftpflicht.value,
        },
        {
          label: 'GAP Deckung',
          fieldPath: 'gapDeckung',
          inputFormatter: boolean,
          formatter: string,
          requirement: () =>
            isNotHaftpflicht.value &&
            getSimpleChoiceMeta(
              SIMPLE_CHOICE.FINANZIERUNGSART,
              SIMPLE_CHOICE_META.FINANZIERUNGSART_NOT_SELF_FINANCING,
            ).includes(useNutzv2InputStore().fahrzeug.finanzierungsart),
        },
        {
          label: 'Rabattschutz',
          fieldPath: 'rabattschutzVollkasko',
          inputFormatter: boolean,
          formatter: string,
          requirement: () => hasRabattschutzKfzVollkasko.value,
        },
        {
          label: 'Selbstbeteiligung Vollkasko',
          fieldPath: 'kaskoSB.vollkasko',
          formatter: (value: string) => formatCurrency(value, true),
          inputFieldAsFallback: true,
          requirement: () => isVollkasko.value,
        },
        {
          label: 'Selbstbeteiligung Teilkasko',
          fieldPath: () =>
            isVollkasko.value && useNutzv2InputStore().kaskoSB.gleicheSB
              ? 'kaskoSB.vollkasko'
              : 'kaskoSB.teilkasko',
          formatter: (value: string) => formatCurrency(value, true),
          inputFieldAsFallback: true,
          requirement: () => isNotHaftpflicht.value,
        },
        {
          label: 'SF-Klasse Haftpflicht',
          fieldPath: 'sfKlasseKH',
          formatter: formatChoice(SIMPLE_CHOICE.SF_KLASSE_KH_V2),
          inputFormatter: formatChoice(SIMPLE_CHOICE.SF_KLASSE_KH_V2),
          inputFieldAsFallback: false,
          requirement: () => hasSfKlasseReactive.value,
        },
        {
          label: 'SF-Klasse Vollkasko',
          fieldPath: 'sfKlasseVK',
          formatter: formatChoice(SIMPLE_CHOICE.SF_KLASSE_VK_V2),
          inputFormatter: formatChoice(SIMPLE_CHOICE.SF_KLASSE_VK_V2),
          inputFieldAsFallback: false,
          requirement: () => hasSfKlasseAndisVollkasko.value,
        },
        {
          label: 'Beitrag Haftpflicht',
          fieldPath: 'beitragHaftpflicht',
          formatter: formatCurrency,
          inputFormatter: string,
          inputFieldAsFallback: false,
        },
        {
          label: 'Beitrag Kasko',
          fieldPath: 'beitragKasko',
          formatter: formatCurrency,
          inputFormatter: string,
          inputFieldAsFallback: false,
          requirement: () => isNotHaftpflicht.value,
        },
        {
          label: 'Zahlweise',
          fieldPath: 'zahlweise',
          formatter: formatChoice(SIMPLE_CHOICE.PAYMENT_PERIOD),
        },
      ],
    },

    baseTariffComponent: () =>
      import('@/features/Tariffs/BaseTariff/BaseTariff.vue'),

    tariffComponent: () =>
      import(
        '@/features/Sparten/shared/components/kfz/notPkw/TariffDetails/TariffDetails.vue'
      ),

    devtools: {
      directAccess: {
        input: {
          icon: 'edit',
          setup: async () => getHelper('nutzv2').resetState(),
        },
        offers: {
          icon: 'list',
          setup: async () => getHelper('nutzv2').selectAllFeatures(),
        },
        comparison: {
          icon: 'compare_arrows',
          setup: async () => getHelper('nutzv2').loadTariffs(),
        },
        contract: {
          icon: 'description',
          setup: async () => getHelper('nutzv2').showContract(),
        },
      },
    },

    contractVnSchema: () =>
      useClientStore().vnIsCompany
        ? VersicherungsnehmerCompanyPropertyContractSchema
        : VersicherungsnehmerPropertyContractSchema,

    hasPreviousInsurance: kfzPreviousInsurance,

    hasAutomaticTermination: () =>
      getSimpleChoiceMeta(
        SIMPLE_CHOICE.VERSICHERUNGSART_VERSICHERER_TARIF_WECHSEL,
        SIMPLE_CHOICE_META.VERSICHERUNGSART_TARIF_VERSICHERER_WECHSEL,
      ).includes(useNutzv2InputStore().allgemein.versicherungsart),

    documentTitlePrefix: () =>
      import('@/features/Sparten/shared/kfz/documentTitlePrefix'),

    requiresBankTransferInformation: () =>
      getSimpleChoiceMeta(
        SIMPLE_CHOICE.PAYMENT_METHOD,
        SIMPLE_CHOICE_META.PAYMENT_METHOD_NEEDS_BANK_DETAILS,
      ).includes(useNutzv2InputStore().zahlart),

    requiresBankTransferPayeeDetails: () => {
      return (
        !isCompany.value ||
        !useClientStore().isSameByRole(
          ROLE.VERSICHERUNGSNEHMER,
          ROLE.ZAHLPERSON,
        )
      );
    },

    enableEVBService: true,
  },

  steps: {
    input: {
      component: () => import('@/features/Sparten/nutzv2/views/InputView.vue'),
    },
    offers: {
      component: () =>
        import('@/features/Sparten/shared/views/kfz/notPkw/Tariffs.vue'),
    },
    comparison: {
      component: () =>
        import('@/features/Sparten/shared/views/kfz/Comparison.vue'),
    },
    contract: {
      component: () =>
        import('@/features/Sparten/shared/views/kfz/ContractView.vue'),
    },
    completion: {
      component: () => import('@/views/Layouts/Completion/Completion.vue'),
    },
  },
};
